import { useEffect, useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { Button, Combobox, Field, Input, Modal, Textarea } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { useUserOptions } from '@/lib/use-user-options';
import { schemeMeasuresRegions } from '@/lib/commissions';
import type {
  CommissionAssignment,
  CommissionAssignmentCreateInput,
  CommissionAssignmentUpdateInput,
  CommissionScheme,
  TargetPeriodRecord,
} from '@/lib/types';

/* ---------------------------------------------------------------------------
 * ONE ASSIGNMENT IS EVERYTHING A NEW JOINER NEEDS — a scheme, a person, a
 * financial year, the two figures on their ladder, and the dates that pro-rate
 * them.
 *
 * Contract facts, from `migrations/203_create_d_commission_assignments.sql` and
 * `commissions.schema.ts` in `emerge-backend-app`:
 *
 *  1. ⛔ `regionalTarget` EMPTY MEANS `null` — NO REGIONAL COMMITMENT — AND IT IS
 *     NOT ZERO. Zero makes regional attainment a division by zero, which MySQL
 *     answers with NULL rather than with an error, so the gate silently never
 *     opens and the lead reads as underperforming for a year that may have been
 *     their best. `null` says the question is not asked and the statement says
 *     so. ⚠️ A `?? 0` or a `?? '0.00'` anywhere between this form and the column
 *     turns a personal-only assignment into one with an unattainable commitment.
 *     `''` is a 422 on this field, NOT a clear — an emptied money box must send
 *     `null`.
 *
 *  2. ⚠️ A SCHEME THAT MEASURES BANDS REQUIRES ONE. The server refuses the pair
 *     and names `regionalTarget`; this form anticipates it in the control's own
 *     state AND still renders the server's sentence when it arrives, because the
 *     scheme's band set can move between this form loading and the save landing.
 *
 *  3. ⛔ DATES ARE PLAIN `YYYY-MM-DD` STRINGS AND NOTHING HERE CONSTRUCTS A
 *     `Date`. `new Date('2026-05-31')` is midnight UTC and renders as 30 May west
 *     of Greenwich; `toISOString().slice(0, 10)` is the same bug mirrored. They
 *     are compared as strings, which sorts correctly by construction.
 *
 *  4. ⚠️ BOTH DATES ARE INDEPENDENTLY NULLABLE AND EITHER ALONE IS A LEGAL
 *     HALF-OPEN RANGE — joined in October and still here, or here from the start
 *     and left in December. `NULL` means the whole period, which is the ORDINARY
 *     case. The pair is never required and the order is checked only when both
 *     are present, exactly as `ck_d_commission_assignments_dates` does.
 *
 *  5. ⛔ THE TARGETS LIVE HERE AND NOT ON `f_revenue_targets`. A management
 *     target steers a dashboard and is edited by any administrator; the figure on
 *     this row is a term somebody signed. ⛔ Do not add a sync, a default or a
 *     copy button between the two — any of those makes one a silent second
 *     writer of the other.
 *
 *  6. `(lead, period)` IS UNIQUE. A collision is a 409 naming the existing
 *     assignment, and that sentence is the whole actionable part.
 *
 *  7. PATCH sends ONLY what changed. An empty patch is a 422, so Save is
 *     disabled when nothing moved.
 * ------------------------------------------------------------------------- */

export interface AssignmentFormModalProps {
  open: boolean;
  /** Absent (or null) = create, a record = edit. */
  assignment?: CommissionAssignment | null;
  /**
   * The schemes a person may assign.
   *
   * ⚠️ OPTIONAL, AND PASSING IT IS THE BETTER HALF. A page that already holds the
   * list should hand it over: the chosen scheme's `billingRegions` is what decides
   * whether a regional target is required, and two independent reads of the same
   * list can disagree about that — the disagreement surfacing as a refusal on a
   * field this form had just called optional. Omitted, the form reads the list
   * itself rather than rendering an empty picker, because an empty picker over a
   * request nobody made is indistinguishable from there being no schemes.
   */
  schemes?: readonly CommissionScheme[];
  /** The scheme list could not be read — "we do not know", never "there are none". */
  schemesFailed?: boolean;
  onClose: () => void;
  onSaved: (saved: CommissionAssignment) => void;
}

/* -------------------------------------------------------------------------- */
/* Form state                                                                 */
/* -------------------------------------------------------------------------- */

interface FormState {
  schemeId: string;
  userId: string;
  periodId: string;
  /** `''` = the whole period (`null`). Plain `YYYY-MM-DD` otherwise. */
  startsOn: string;
  /** `''` = the whole period (`null`). See `startsOn`. */
  endsOn: string;
  /** FULL YEAR, in the scheme's currency. Strictly positive. */
  personalTarget: string;
  /** ⛔ `''` = NO REGIONAL COMMITMENT (`null`), and that is not zero. */
  regionalTarget: string;
  notes: string;
}

/**
 * ⚠️ EVERY KEY IS A WRITE KEY SPELLED AS THE API SPELLS IT, and
 * `KNOWN_WRITE_KEYS` is derived from here rather than listed twice.
 */
function emptyValues(): FormState {
  return {
    schemeId: '',
    userId: '',
    periodId: '',
    startsOn: '',
    endsOn: '',
    personalTarget: '',
    regionalTarget: '',
    notes: '',
  };
}

function recordForm(record: CommissionAssignment): FormState {
  return {
    schemeId: String(record.scheme.id),
    userId: String(record.user.id),
    periodId: String(record.period.id),
    startsOn: record.startsOn ?? '',
    endsOn: record.endsOn ?? '',
    personalTarget: record.personalTarget,
    // ⛔ `?? ''` here is the ONLY safe direction: `null` is "no commitment" and
    // an empty box is what says so on screen. It is never `?? '0'`.
    regionalTarget: record.regionalTarget ?? '',
    notes: record.notes ?? '',
  };
}

const KNOWN_WRITE_KEYS = Object.keys(emptyValues());

const LABELS: Record<string, string> = {
  schemeId: 'Scheme',
  userId: 'Sales lead',
  periodId: 'Financial year',
  startsOn: 'Starts on',
  endsOn: 'Ends on',
  personalTarget: 'Personal target',
  regionalTarget: 'Regional target',
  notes: 'Notes',
};

const NOTES_MAX = 2_000;

/** `DECIMAL(14,2)`: at most 12 integer digits and 2 decimals, and NO sign. */
const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * Is this money string above zero?
 *
 * LEXICAL, because nothing on this path parses a figure. With no sign permitted
 * and at most one decimal point, any digit from 1 to 9 anywhere in the string
 * means the amount is above zero; `'0'`, `'0.00'` and `'000.0'` are the only
 * shapes that are not. The same test the server's own schema uses.
 */
function isAboveZero(value: string): boolean {
  return /[1-9]/.test(value);
}

/* -------------------------------------------------------------------------- */
/* Payload shape                                                              */
/* -------------------------------------------------------------------------- */

/*
 * ⚠️ HANDED STRAIGHT TO THE API METHOD RATHER THAN CAST. The assignment below to
 * `CommissionAssignmentUpdateInput` is the one instrument that checks these keys
 * against the real write contract, which lives in a file this one does not own.
 */
interface AssignmentPatch {
  schemeId?: number;
  userId?: number;
  periodId?: number;
  startsOn?: string | null;
  endsOn?: string | null;
  personalTarget?: string;
  regionalTarget?: string | null;
  notes?: string | null;
}

/* -------------------------------------------------------------------------- */
/* The modal                                                                  */
/* -------------------------------------------------------------------------- */

export function AssignmentFormModal({
  open,
  assignment,
  schemes,
  schemesFailed = false,
  onClose,
  onSaved,
}: AssignmentFormModalProps) {
  const record = assignment ?? null;
  const isEdit = record !== null;
  const formId = useId();

  const [form, setForm] = useState<FormState>(() =>
    record ? recordForm(record) : emptyValues(),
  );
  const [baseline, setBaseline] = useState<FormState>(() =>
    record ? recordForm(record) : emptyValues(),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [periods, setPeriods] = useState<readonly TargetPeriodRecord[]>([]);
  const [periodsFailed, setPeriodsFailed] = useState(false);

  /*
   * Only read when the caller did not supply the list. `schemes === undefined`
   * is "nobody handed me one", which is a different fact from `[]` — a caller
   * that genuinely holds no schemes passes the empty array and this form must
   * not go behind its back and fetch a second answer.
   */
  const [fetchedSchemes, setFetchedSchemes] = useState<readonly CommissionScheme[]>([]);
  const [fetchedSchemesFailed, setFetchedSchemesFailed] = useState(false);
  const [schemesTruncated, setSchemesTruncated] = useState(false);

  const staff = useUserOptions(open);

  const schemeList = schemes ?? fetchedSchemes;
  const schemesUnreadable = schemesFailed || (schemes === undefined && fetchedSchemesFailed);

  useEffect(() => {
    if (!open || schemes !== undefined) return;
    const controller = new AbortController();
    api
      /*
       * ⚠️ `perPage` IS THE ENTITY CEILING OF 100, NOT A MASTER'S 200 —
       * `d_commission_schemes` is a record table however template-shaped it reads.
       * So this picker can legitimately be SHORT, and `schemesTruncated` is what
       * says so rather than a bigger number: 100 is the server's own ceiling and
       * there is no larger value to reach for. `/projects` walked into the same
       * wall at 113 rows and the fix there was paging, never a wider request.
       */
      .listCommissionSchemes({ perPage: 100 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setFetchedSchemes(result.data);
        setSchemesTruncated(result.meta.totalPages > 1);
        setFetchedSchemesFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setFetchedSchemesFailed(true);
        setSchemesTruncated(false);
      });
    return () => controller.abort();
  }, [open, schemes]);

  /*
   * RE-SEED ON OPEN, AND ON A DIFFERENT RECORD — during render, not in an effect.
   *
   * ⚠️ THE CALL SITE KEEPS THIS MOUNTED ACROSS OPENS, so lazy initial state alone
   * would show the previously edited assignment's targets under the new lead's
   * name. ⚠️ And an effect would re-seed AFTER a paint, so those figures would
   * briefly be on screen and `patch`, `dirty` and `submitDisabled` would all be
   * computed against them for that render. React's own adjust-state-during-render
   * pattern discards the render instead; the guard is what makes it terminate.
   */
  const openIdentity = open ? (record ? `edit:${record.id}` : 'new') : 'closed';
  const [seededFor, setSeededFor] = useState(openIdentity);
  if (seededFor !== openIdentity) {
    const next = record ? recordForm(record) : emptyValues();
    setSeededFor(openIdentity);
    setForm(next);
    setBaseline(next);
    setErrors({});
    setFormError(null);
  }

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      /*
       * ⚠️ NO `isActive` FILTER AND NO `sort`. The stored period has to resolve
       * to a NAME whatever the master now says — a retired year on a live
       * assignment must not render as a blank picker — so the whole list is read
       * and the RETIRED rows are filtered at render instead, keeping the held
       * value. The ordering is done here too rather than through `?sort=`: the
       * accepted sort members are the server's enum and a wrong one is a 422
       * that takes out the entire read.
       */
      .listTargetPeriods({ perPage: 200 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setPeriods(result.data);
        setPeriodsFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setPeriodsFailed(true);
      });
    return () => controller.abort();
  }, [open]);

  /* ---- options --------------------------------------------------------- */

  const schemeOptions = useMemo<ComboboxOption[]>(() => {
    const options = schemeList
      .filter((item) => item.isActive || String(item.id) === form.schemeId)
      .map((item) => ({
        value: String(item.id),
        label: item.isActive ? item.name : `${item.name} (retired)`,
        hint: item.currency,
      }));
    // The scheme this assignment already names, when the list did not carry it.
    if (record && !options.some((option) => option.value === String(record.scheme.id))) {
      options.unshift({
        value: String(record.scheme.id),
        label: record.scheme.name,
        hint: record.scheme.currency,
      });
    }
    return options;
  }, [schemeList, form.schemeId, record]);

  const leadOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = staff.users.map((user) => ({
      value: String(user.id),
      label: user.name.trim() === '' ? user.email : user.name,
      hint: user.email,
    }));
    /*
     * ⚠️ THE STORED LEAD IS INJECTED BACK. The directory is one page and this
     * picker filters it locally, so somebody past the cap would otherwise read as
     * unassigned — and the next save would make that true.
     */
    if (record && !options.some((option) => option.value === String(record.user.id))) {
      options.unshift({
        value: String(record.user.id),
        label: record.user.name ?? record.user.email,
        hint: record.user.email,
      });
    }
    return options;
  }, [staff.users, record]);

  const periodOptions = useMemo<ComboboxOption[]>(() => {
    const rows = [...periods].sort((a, b) => b.startDate.localeCompare(a.startDate));
    const options = rows
      .filter((period) => period.isActive || String(period.id) === form.periodId)
      .map((period) => ({
        value: String(period.id),
        label: period.isActive ? period.name : `${period.name} (retired)`,
        hint: `${period.startDate} to ${period.endDate}`,
      }));
    if (record && !options.some((option) => option.value === String(record.period.id))) {
      options.unshift({
        value: String(record.period.id),
        label: record.period.name,
        hint: `${record.period.startDate} to ${record.period.endDate}`,
      });
    }
    return options;
  }, [periods, form.periodId, record]);

  const selectedScheme =
    schemeList.find((item) => String(item.id) === form.schemeId) ?? null;

  /**
   * ⚠️ WHETHER A REGIONAL TARGET IS REQUIRED IS A PROPERTY OF THE CHOSEN SCHEME,
   * not of this form. A scheme measuring no bands has no regional pool at all, so
   * a commitment against one would be a figure nothing computes against.
   *
   * ⚠️ `null` here means "we cannot tell" — the scheme list has not arrived, or
   * the chosen scheme is not in it — and it is NOT `false`. Treating unknown as
   * "not required" is how a form calls a field optional and then meets a 422 on
   * it; treating it as "required" would refuse a legal personal-only assignment.
   * Unknown asks for nothing and lets the server answer.
   */
  const regionalTargetRequired: boolean | null =
    /*
     * ⛔ `schemeMeasuresRegions`, NEVER `billingRegions.length > 0` WRITTEN OUT
     * HERE. A bare length check reads as an emptiness test on an array, so the
     * day the rule moves — an older server sending `null`, or a scheme whose
     * bands have all been retired — it has to be found at every site rather than
     * changed in one. The named test is the module's own instruction.
     */
    selectedScheme === null ? null : schemeMeasuresRegions(selectedScheme);

  /* ---- diffing --------------------------------------------------------- */

  const patch = useMemo<AssignmentPatch | null>(() => {
    if (!isEdit) return null;
    const body: AssignmentPatch = {};

    if (form.schemeId !== baseline.schemeId && form.schemeId !== '') {
      body.schemeId = Number(form.schemeId);
    }
    if (form.userId !== baseline.userId && form.userId !== '') {
      body.userId = Number(form.userId);
    }
    if (form.periodId !== baseline.periodId && form.periodId !== '') {
      body.periodId = Number(form.periodId);
    }
    if (form.startsOn !== baseline.startsOn) {
      body.startsOn = form.startsOn === '' ? null : form.startsOn;
    }
    if (form.endsOn !== baseline.endsOn) {
      body.endsOn = form.endsOn === '' ? null : form.endsOn;
    }
    if (form.personalTarget.trim() !== baseline.personalTarget.trim()) {
      body.personalTarget = form.personalTarget.trim();
    }
    if (form.regionalTarget.trim() !== baseline.regionalTarget.trim()) {
      /*
       * ⛔ THE EMPTY BOX SENDS `null`, NEVER `''` AND NEVER `'0'`. `''` is a 422
       * on this field and `0` is a commitment nobody can attain. `null` is the
       * only spelling of "no regional commitment".
       */
      body.regionalTarget =
        form.regionalTarget.trim() === '' ? null : form.regionalTarget.trim();
    }
    if (form.notes.trim() !== baseline.notes.trim()) {
      body.notes = form.notes.trim() === '' ? null : form.notes.trim();
    }

    return body;
  }, [form, baseline, isEdit]);

  const dirty = patch !== null && Object.keys(patch).length > 0;

  const requiredBlank =
    form.schemeId === '' ||
    form.userId === '' ||
    form.periodId === '' ||
    form.personalTarget.trim() === '';

  const submitDisabled = saving || (isEdit && !dirty && !requiredBlank);

  /* ---- setters --------------------------------------------------------- */

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  /* ---- validation ------------------------------------------------------ */

  function validate(state: FormState): Record<string, string> {
    const found: Record<string, string> = {};

    if (state.schemeId === '') found.schemeId = 'Choose a scheme.';
    if (state.userId === '') found.userId = 'Choose the sales lead.';
    if (state.periodId === '') found.periodId = 'Choose the financial year.';

    const personal = state.personalTarget.trim();
    if (personal === '') {
      found.personalTarget = 'A personal target is required.';
    } else if (!MONEY_PATTERN.test(personal)) {
      found.personalTarget = 'Enter an amount with at most 2 decimal places, and no minus sign.';
    } else if (!isAboveZero(personal)) {
      /*
       * A REFUSAL, and it names the consequence because the figure looks legal.
       * A target of zero makes attainment a division by zero, which the database
       * answers with NULL rather than an error — so the gate never opens and
       * nothing on any screen looks wrong.
       */
      found.personalTarget =
        'A personal target must be above zero — a target of zero makes attainment unmeasurable and the gate never opens.';
    }

    const regional = state.regionalTarget.trim();
    if (regional !== '') {
      if (!MONEY_PATTERN.test(regional)) {
        found.regionalTarget = 'Enter an amount with at most 2 decimal places, and no minus sign.';
      } else if (!isAboveZero(regional)) {
        found.regionalTarget =
          'A regional target must be above zero. Leave it empty for no regional commitment — that is not the same as zero.';
      }
    } else if (regionalTargetRequired === true) {
      // Anticipating the server's own refusal, said where it can be acted on.
      found.regionalTarget =
        'This scheme measures a regional pool, so it needs a regional target.';
    }

    /*
     * ⚠️ COMPARED ONLY WHEN BOTH ARE PRESENT. Either alone is a legal half-open
     * range, and a check that refused those would refuse the ordinary part year
     * these columns exist for. String comparison is correct for `YYYY-MM-DD` and
     * is the reason nothing here builds a `Date`.
     */
    if (state.startsOn !== '' && state.endsOn !== '' && state.endsOn < state.startsOn) {
      found.endsOn = 'The end date is before the start date.';
    }

    if (state.notes.trim().length > NOTES_MAX) {
      found.notes = `Keep this to ${NOTES_MAX} characters.`;
    }

    return found;
  }

  /* ---- submit ---------------------------------------------------------- */

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /*
     * ⚠️ React propagates by the REACT tree even through a portal, so a submit
     * from a dialog opened out of this one would reach a form behind it. This
     * repo created a half-filled record that way, with `tsc` and oxlint green.
     */
    event.stopPropagation();
    if (saving) return;

    const clientErrors = validate(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);

    try {
      if (record) {
        const body = patch ?? {};
        // An empty patch is a guaranteed 422 and must never leave the client.
        if (Object.keys(body).length === 0) {
          setSaving(false);
          return;
        }
        const update: CommissionAssignmentUpdateInput = body;
        const result = await api.updateCommissionAssignment(record.id, update);
        onSaved(result.data);
        onClose();
        return;
      }

      const create: CommissionAssignmentCreateInput = {
        schemeId: Number(form.schemeId),
        userId: Number(form.userId),
        periodId: Number(form.periodId),
        startsOn: form.startsOn === '' ? null : form.startsOn,
        endsOn: form.endsOn === '' ? null : form.endsOn,
        personalTarget: form.personalTarget.trim(),
        // ⛔ `null`, never `'0'`. See the note in the patch builder.
        regionalTarget: form.regionalTarget.trim() === '' ? null : form.regionalTarget.trim(),
        notes: form.notes.trim() === '' ? null : form.notes.trim(),
      };

      const result = await api.createCommissionAssignment(create);
      onSaved(result.data);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const { fields, unroutable } = error.routeFieldErrors(KNOWN_WRITE_KEYS);
        setErrors(fields);

        const extras = unroutable.filter((line) => line !== error.message);

        if (error.status === 403) {
          setFormError('You do not have permission to save commission assignments.');
        } else if (error.status === 404) {
          setFormError('This assignment no longer exists.');
        } else if (error.status === 409) {
          /*
           * ⚠️ VERBATIM. `(lead, period)` is UNIQUE — one scheme per lead per
           * year, because two assignments would produce two statements in a run
           * and TWO PAYMENTS, both internally consistent. The server's sentence
           * NAMES the assignment that already exists, which is the only
           * actionable part, and a 409 carries no `details` to mark a field with.
           */
          setFormError(error.message);
        } else if (extras.length > 0) {
          setFormError(`${error.message} — ${extras.join('; ')}`);
        } else if (unroutable.length > 0) {
          setFormError(error.message);
        } else if (Object.keys(fields).length > 0) {
          setFormError(null);
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  /* ---- render helpers -------------------------------------------------- */

  /**
   * ⛔ NOT HELPER TEXT — the fourth kind: A FACT ABOUT THE DATA THE READER CANNOT
   * OTHERWISE SEE, and a sweep must not delete it.
   *
   * An empty money box normally means "nothing yet". Here it means something
   * specific and irreversible-looking on the statement: the regional pool is not
   * computed at all, and the gate is not tested against a commitment. Nothing
   * else on this screen distinguishes that from a figure somebody has not typed,
   * and `0` — the value a reader would otherwise reach for — is a silent
   * permanent gate failure. It renders only while the box is empty.
   */
  const regionalTargetNote =
    form.regionalTarget.trim() === '' && regionalTargetRequired !== true
      ? 'Empty means no regional commitment: the pool is not computed. That is not the same as 0.'
      : undefined;

  /**
   * ⛔ A REFUSAL, not helper text: the directory could not be read, so a picker
   * showing nobody is not a statement about who is on staff.
   */
  const leadError =
    errors.userId ??
    (staff.failed ? 'The staff directory could not be read.' : undefined);

  const leadFootnote =
    staff.hasMore && !staff.failed
      ? 'Showing the first page of staff — this list is searched locally.'
      : undefined;

  const schemeError =
    errors.schemeId ?? (schemesUnreadable ? 'The scheme list could not be read.' : undefined);

  /**
   * ⛔ NOT HELPER TEXT — a FACT about the data: the list is one page and this
   * control filters it locally, so a scheme past the ceiling is not merely absent
   * from the dropdown, it is unpickable. Silence here would read as the scheme
   * not existing.
   */
  const schemeFootnote =
    schemesTruncated && !schemesUnreadable
      ? 'Showing the first 100 schemes — this list is searched locally.'
      : undefined;

  const periodError =
    errors.periodId ?? (periodsFailed ? 'The financial years could not be read.' : undefined);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={isEdit ? 'Edit commission assignment' : 'New commission assignment'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            loading={saving}
            disabled={submitDisabled}
          >
            {isEdit ? 'Save changes' : 'Create assignment'}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="grid min-w-0 gap-3">
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}

        <Section title="Who, and for which year">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={LABELS.schemeId ?? 'Scheme'} required error={schemeError}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.schemeId}
                  options={schemeOptions}
                  {...(schemeFootnote ? { footnote: schemeFootnote } : {})}
                  disabled={saving}
                  invalid={Boolean(schemeError)}
                  onChange={(value) => setField('schemeId', value)}
                />
              )}
            </Field>

            <Field label={LABELS.userId ?? 'Sales lead'} required error={leadError}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.userId}
                  options={leadOptions}
                  loading={staff.loading}
                  {...(leadFootnote ? { footnote: leadFootnote } : {})}
                  disabled={saving}
                  invalid={Boolean(leadError)}
                  onChange={(value) => setField('userId', value)}
                />
              )}
            </Field>

            <div className="sm:col-span-2">
              <Field label={LABELS.periodId ?? 'Financial year'} required error={periodError}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={form.periodId}
                    options={periodOptions}
                    disabled={saving}
                    invalid={Boolean(periodError)}
                    onChange={(value) => setField('periodId', value)}
                  />
                )}
              </Field>
            </div>
          </div>
        </Section>

        <Section title="Part of the year served">
          {/*
            ⚠️ NEITHER DATE IS REQUIRED AND NEITHER IMPLIES THE OTHER. Empty means
            the whole period, which is the ordinary case — these narrow BOTH the
            target and the revenue window, so a lead joining in October carries
            half the target and earns against that half.
          */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={LABELS.startsOn ?? 'Starts on'} error={errors.startsOn}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="startsOn"
                  type="date"
                  value={form.startsOn}
                  disabled={saving}
                  invalid={Boolean(errors.startsOn)}
                  onChange={(event) => setField('startsOn', event.target.value)}
                />
              )}
            </Field>

            <Field label={LABELS.endsOn ?? 'Ends on'} error={errors.endsOn}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="endsOn"
                  type="date"
                  value={form.endsOn}
                  disabled={saving}
                  invalid={Boolean(errors.endsOn)}
                  onChange={(event) => setField('endsOn', event.target.value)}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section title="The ladder">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={
                selectedScheme
                  ? `${LABELS.personalTarget ?? 'Personal target'} (${selectedScheme.currency})`
                  : (LABELS.personalTarget ?? 'Personal target')
              }
              required
              error={errors.personalTarget}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="personalTarget"
                  value={form.personalTarget}
                  inputMode="decimal"
                  autoComplete="off"
                  disabled={saving}
                  invalid={Boolean(errors.personalTarget)}
                  onChange={(event) => setField('personalTarget', event.target.value)}
                />
              )}
            </Field>

            <Field
              label={
                selectedScheme
                  ? `${LABELS.regionalTarget ?? 'Regional target'} (${selectedScheme.currency})`
                  : (LABELS.regionalTarget ?? 'Regional target')
              }
              required={regionalTargetRequired === true}
              hint={regionalTargetNote}
              error={errors.regionalTarget}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="regionalTarget"
                  value={form.regionalTarget}
                  inputMode="decimal"
                  autoComplete="off"
                  disabled={saving}
                  invalid={Boolean(errors.regionalTarget)}
                  onChange={(event) => setField('regionalTarget', event.target.value)}
                />
              )}
            </Field>

            <div className="sm:col-span-2">
              <Field label={LABELS.notes ?? 'Notes'} error={errors.notes}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Textarea
                    id={id}
                    aria-describedby={describedBy}
                    name="notes"
                    rows={3}
                    maxLength={NOTES_MAX}
                    value={form.notes}
                    disabled={saving}
                    invalid={Boolean(errors.notes)}
                    onChange={(event) => setField('notes', event.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>
        </Section>
      </form>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="min-w-0 rounded-lg border border-border bg-surface">
      <legend className="mx-3 px-1 text-xs font-semibold text-fg">{title}</legend>
      <div className="min-w-0 px-3 pt-1 pb-3">{children}</div>
    </fieldset>
  );
}
